<template>
  <div
    class="product-options"
    :class="{ [$class('loading')]: loading }"
  >
    <div
      v-for="(option) of options"
      :key="getOptionKey(option)"
      class="product-options__option"
      :class="{ [$class('hidden')]: !getDisplayOption(option) }"
    >
      <legend
        v-if="optionLegend"
        class="product-options__option-title text-body-m-bold"
        v-text="optionLegend"
      />

      <div class="product-options__values">
        <selection-tab
          v-for="(value, index) of option.values"
          :id="getOptionValueKey(value, index, option)"
          :key="getOptionValueKey(value, index, option)"
          v-model="currentOptions[option.index]"
          class="product-options__value"
          :disabled="getValueDisabled(option.disabled[value], 'disabled')"
          :label="getOptionLabel(value)"
          :name="getOptionKey(option)"
          :country ="country"
          :location="location"
          :value="value"
          :variant= product.variants
          :visually-disabled="getValueDisabled(option.disabled[value], 'visually-disabled')"
        />
      </div>
    </div>

    <select
      v-model="currentVariantId"
      class="product-options__variant-select visually-hidden"
      name="id"
    >
      <option
        v-for="(variantObject, index) of product.variants"
        :key="getVariantKey(variantObject, index)"
        :value="variantObject.id"
        v-text="`${variantObject.title} - ${variantObject.id}`"
      />
    </select>
  </div>
</template>

<script>

/**
 * Vue: Product options (product-form)
 * -----------------------------------------------------------------------------
 * Product variants split into separate options and values.
 *
 * @param {Boolean} [loading] - Loading state.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object} section - Main product section settings.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 * @emits updateVariant - Emit new variant object.
 *
 */
import SelectionTab from '~async/utils/selection-tab/selection-tab'
import { formatNoDecimal } from '~/helpers/format'
import { convertToHandle } from '~/helpers/convert'
import { emitUpdateVariant } from '~/helpers/vue'

export default {
  name: 'ProductOptions',

  components: {
    SelectionTab,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    section: {
      type: Object,
      default: () => ({
        displaySingleOptionNames: false,
        displaySingleValueOptions: false,
        displaySingleVariantOption: false,
        excludedInitialSelectOptions: [],
        initialSelect: true,
        selectDisabledValues: true,
      }),
    },
    country :{
      type: String ,
      default:''
    },
    location :{
      type: String ,
      default:''
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'update-variant': emitUpdateVariant,
  },

  data() {
    return {
      currentVariantId: 0,
      currentOptions: [],
      options: [],
      currencysymbol: cnvs.store.currencySymbol,
    }
  },

  computed: {

    /**
     * Get option legend.
     * @returns {String}
     */
    optionLegend() {
      let legend = this.$string('product.sidebar.select_option')
      if (this.product.type && (this.product.type || this.liquid.type) === 'sawmill') {
        legend = this.$string('product.sidebar.select_engine')
      }

      return legend
    },
  },

  watch: {

    /**
     * Watch current options to update based on option choices.
     */
    currentOptions: {
      handler() {
        this.getOptions()
        const variantTitle = this.currentOptions.join(' / ')
        const variant = this.getVariantFromProperty(variantTitle, 'title')

        if (!variant) {
          return
        }

        this.currentVariantId = variant.id
      },
      deep: true,
    },

    /**
     * Watch currentVariantId to update Canvas variant when select changes.
     */
    currentVariantId() {
      if (this.variant.id === this.currentVariantId) {
        return
      }

      const variant = this.getVariantFromProperty(this.currentVariantId)

      if (!variant) {
        return
      }

      this.$emit('update-variant', variant)
    },

    /**
     * Watch product to update selected variant when product data is loaded.
     */
    product() {
      this.currentOptions = []
      this.getCurrentVariant()
    },

    /**
     * Watch variant for updates to update current options.
     */
    variant() {
      if (!this.variant) {
        this.currentVariantId = 0
      }

      this.getCurrentOptions()
    },
  },

  mounted() {
    this.getCurrentVariant()
    this.getOptions()
    //console.log('product ption ', this.country ,'and ', this.location)
  },

  methods: {

    /**
     * Get if single value options should be displayed.
     * @param {Object} option - Option object.
     * @returns {Boolean}
     */
    getDisplayOption(option) {
      if (this.section.displaySingleValueOptions) {
        return true
      }

      return option.values.length > 1
    },

    /**
     * Get option key.
     * @param {Object} option - Option object.
     * @returns {String}
     */
    getOptionKey(option) {
      return `${this.product.handle}-option-${option.index}-${convertToHandle(option.name)}`
    },

    /**
     * Get option value key/id.
     * @param {String} value - Value name.
     * @param {Number} index - Forloop index.
     * @param {Object} option - Option object.
     * @returns {String}
     */
    getOptionValueKey(value, index, option) {
      return `${this.getOptionKey(option)}-value-${index}-${convertToHandle(value)}`
    },

    /**
     * Get variant key.
     * @param {Object} variant - Variant object.
     * @param {Number} index - Forloop index.
     * @returns {String}
     */
    getVariantKey(variant, index) {
      return `${this.product.handle}-option-${index}-${convertToHandle(variant.title)}`
    },

    /**
     * Get if value is disabled.
     * - Based on section setting and attribute.
     * @param {Boolean} available - Value availability.
     * @param {String} attribute - Seleection tab attribute.
     * @returns {Boolean}
     */
    getValueDisabled(available, attribute) {
      if (!available) {
        return false
      }

      return (
        (attribute === 'disabled' && !this.section.selectDisabledValues) ||
        (attribute === 'visually-disabled' && this.section.selectDisabledValues)
      )
    },

    /**
     * Get current variant on page load or product update.
     */
    getCurrentVariant() {
      if (!this.product || !this.product?.variants?.length) {
        return
      }

      if (cnvs.storybook && this.variant) {
        this.getCurrentOptions()
        return
      }

      /**
       * Always get current variant from URL.
       */
      const variantFromUrl = this.getVariantFromUrl()

      if (variantFromUrl) {
        this.currentVariantId = variantFromUrl.id
        return
      }

      /**
       * Do not set current variant if disabled in settings.
       */
      if (!this.section.initialSelect) {
        return
      }

      /**
       * Pick the first in stock variant.
       * - If no variants are in stock then use first variant.
       */
      const inStock = this.product.variants.filter((variant) => variant.available)
      const currentVariant = inStock.length ? inStock[0] : this.product.variants[0]

      /**
       * Only set current variant if none of the variant options are excluded
       * from being initially set.
       */
      const currentOptions = currentVariant.selectedOptions.map((selectedOption) => {
        const optionHandle = convertToHandle(selectedOption.name)

        return this.section.excludedInitialSelectOptions?.includes(optionHandle)
          ? ''
          : selectedOption.value
      })

      const filteredCurrentOptions = currentOptions.filter(Boolean)

      if (filteredCurrentOptions.length !== currentVariant.selectedOptions.length) {
        this.currentOptions = currentOptions
        return
      }

      this.currentVariantId = currentVariant.id
      this.getCurrentOptions()
    },

    /**
     * Get initial variant from URL parameter.
     * @returns {Object}
     */
    getVariantFromUrl() {
      const params = new URLSearchParams(document.location.search.substring(1))
      const variantId = Number(params.get('variant'))
      return this.getVariantFromProperty(variantId)
    },

    /**
     * Get variant object from variant property.
     * @param {String} value - Value to match.
     * @param {String} property - Property to find by.
     * @returns {Object}
     */
    getVariantFromProperty(value, property = 'id') {
      return this.product.variants?.find((variant) => {
        return variant[property] === value
      })
    },

    /**
     * Get current options.
     */
    getCurrentOptions() {
      if (!this.variant) {
        this.currentOptions = []
        return
      }

      this.variant.selectedOptions.forEach((selectedOption, index) => {
        this.currentOptions[index] = selectedOption.value
      })
    },

    /**
     * Get extended options data.
     * - Extends product data with disabled data.
     * @returns {Array}
     */
    getOptions() {
      if (!this.product?.options?.length) {
        this.options = []
        return
      }

      this.options = this.product.options.map((option, optionIndex) => {
        const disabled = {}

        /**
         * Compute if option is unavailable.
         * - First find variants that match current value.
         * - Then find variants that match other currentOptions.
         * - Then test to see if all matching variants are unavailable.
         */
        option.values.forEach((value) => {
          disabled[value] = this.product.variants
            .filter((variant) => variant.selectedOptions[optionIndex].value === value)
            .filter((variant) => {
              return this.currentOptions.every((currentOption, currentOptionIndex) => {
                if (!currentOption || optionIndex === currentOptionIndex) {
                  return true
                }

                return variant.selectedOptions[currentOptionIndex]?.value === currentOption
              })
            })
            .every((variant) => !variant.available)
        })

        /**
         * Return extended data.
         */
        return {
          ...option,
          disabled,
        }
      })
    },

    getOptionLabel(value) {
      if (this.product.type && (this.product.type || this.liquid.type) === 'sawmill') {
        const variant = this.product.variants.find((variant) => variant.title == value)
        if (value.includes(')')) {
          const splitValue = value.indexOf(')')
          if(variant.compareAtPrice?.amount){

          return `
            <span class="selection-tab__label-maintext text-heading-s text-heading-m-desktop">${value.slice(0, splitValue)})</span>
            <span class="selection-tab__label-subtext text-body-l-regular mt-m">${value.slice(splitValue + 1)}</span>
            <span class="product-prices selection-tab__price mt-m"><span class="product-prices__price text-heading-2xs product-prices__price--on-sale"><span class="product-prices__value">${this.currencysymbol}${formatNoDecimal(this.$formatMoney(variant.price?.amount)).replace(cnvs.store.currencySymbol, '')}</span></span><span class="product-prices__compare-at-price text-heading-2xs"><span class="product-prices__value">${this.currencysymbol}${formatNoDecimal(this.$formatMoney(variant.compareAtPrice?.amount)).replace(cnvs.store.currencySymbol, '')}</span></span></span>
          `

          }else{

          return `
            <span class="selection-tab__label-maintext text-heading-s text-heading-m-desktop">${value.slice(0, splitValue)})</span>
            <span class="selection-tab__label-subtext text-body-l-regular mt-m">${value.slice(splitValue + 1)}</span>
          <span class="product-prices mt-m selection-tab__price"><span class="product-prices__price text-heading-2xs"><span class="product-prices__value">${this.currencysymbol}${formatNoDecimal(this.$formatMoney(variant.price?.amount)).replace(cnvs.store.currencySymbol, '')}</span></span></span>`
          }

        }
      } else {
        return `
            <span class="selection-tab__label-maintext text-heading-s text-heading-m-desktop">${value}</span>
          `
      }

      return value
    },
  },
}
</script>

<style lang="scss">
@import './product-options';
</style>
